import { motion } from "framer-motion";

export default function EtPhilosophy() {

  // First Created - 22 Nov 2025
  // Author - Atul Pandey
  // Purpose — Et Philosophy
  // Updated - 22 Nov 2025 - Cinematic Purpose-Native Philosophy Section
  // Updated - 23 Nov 2025 - Copy and visual refinements

  return (
    <section className="relative overflow-hidden py-32 bg-black text-white">

      {/* --- 1. Background Gradient Layer (Warm Cinematic) --- */}
      <div className="absolute inset-0 bg-gradient-to-b from-black via-orange-950/10 to-black"></div>

      {/* --- 2. Warm Bottom Haze --- */}
      <div
        className="
          absolute bottom-0 left-1/2 -translate-x-1/2
          w-[900px] h-[450px]
          bg-orange-900/20 blur-[180px]
        "
      ></div>

      {/* --- 3. Curved Light Arcs (Warmer Amber) --- */}
      <div className="pointer-events-none absolute -top-40 left-1/2 -translate-x-1/2 w-[900px] h-[900px] rounded-full border border-orange-600/15 blur-[2px]"></div>
      <div className="pointer-events-none absolute -top-60 left-1/2 -translate-x-1/2 w-[1100px] h-[1100px] rounded-full border border-orange-500/10 blur-[2px]"></div>

      {/* --- 4. Ambient Sparks (Warm, Fewer) --- */}
      <div className="pointer-events-none absolute inset-0">
        {[...Array(80)].map((_, i) => (
          <div
            key={i}
            className="absolute w-[3px] h-[3px] bg-orange-500/30 rounded-full animate-philo-spark"
            style={{
              top: `${Math.random() * 100}%`,
              left: `${Math.random() * 100}%`,
              animationDelay: `${Math.random() * 6}s`,
              animationDuration: `${6 + Math.random() * 6}s`,
            }}
          ></div>
        ))}
      </div>


      {/* --- 5. Main Content Container --- */}
      <div className="relative z-20 max-w-5xl mx-auto px-6">

        {/* Title */}
        <motion.h2
          className="text-center text-3xl md:text-4xl font-bold text-orange-500 mb-10 drop-shadow-[0_0_15px_rgba(255,125,0,0.25)]"
          initial={{ opacity: 0, y: 25 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 1 }}
        >
          Our Philosophy : Purpose-Native Enterprise
        </motion.h2>

        {/* Narrative */}
        {/* 23 Nov 2025 */}
        <motion.div
          className="text-lg text-gray-300 leading-relaxed space-y-8 max-w-3xl mx-auto font-extralight"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={{
            hidden: {},
            visible: {
              transition: { staggerChildren: 0.25 }
            }
          }}
        >

          <motion.p
            variants={{
              hidden: { opacity: 0, y: 20 },
              visible: { opacity: 1, y: 0, transition: { duration: 1 } }
            }}
          >
            Performance is not where growth begins. It is the result of 
            <span className="text-orange-600"> clarity</span>.
          </motion.p>

          <motion.p
            variants={{
              hidden: { opacity: 0, y: 20 },
              visible: { opacity: 1, y: 0, transition: { duration: 1 } }
            }}
          >
            Growth becomes meaningful when leaders shift from reaction to 
            <span className="text-orange-600"> intention</span>. From noise to focus. 
            From scattered motion to 
            <span className="text-orange-600"> purposeful progress</span>.
          </motion.p>

          <motion.p
            variants={{
              hidden: { opacity: 0, y: 20 },
              visible: { opacity: 1, y: 0, transition: { duration: 1 } }
            }}
          >
            A <span className="text-orange-600"> purpose native enterprise</span> is one where people
            know what they are doing. Why they are doing it. And how each step connects to 
            something larger than themselves. Thinking, actions, habits, and decisions move in 
            the same direction. With 
            <span className="text-orange-600"> coherence</span>.
          </motion.p>

          <motion.p
            variants={{
              hidden: { opacity: 0, y: 20 },
              visible: { opacity: 1, y: 0, transition: { duration: 1 } }
            }}
          >
            This is the core of our philosophy. When leaders operate with 
            <span className="text-orange-600"> clarity of purpose</span>, 
            <span className="text-orange-600"> consistency of behavior</span>, and 
            <span className="text-orange-600"> visibility of progress</span>, teams thrive. Outcomes 
            accelerate. Growth becomes 
            <span className="text-orange-600"> inevitable</span>.
          </motion.p>

        </motion.div>


        {/* <motion.div
          className="text-lg text-gray-300 leading-relaxed space-y-8 max-w-4xl mx-auto font-extralight"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={{
            hidden: {},
            visible: {
              transition: { staggerChildren: 0.25 }
            }
          }}
        >
          <motion.p
            variants={{
              hidden: { opacity: 0, y: 20 },
              visible: { opacity: 1, y: 0, transition: { duration: 1 } }
            }}
          >
            We believe performance is not the starting point — it is the outcome.
            True, meaningful growth begins when leaders move from reaction to
            intention, from noise to clarity, and from scattered motion to
            purposeful progress.
          </motion.p>

          <motion.p
            variants={{
              hidden: { opacity: 0, y: 20 },
              visible: { opacity: 1, y: 0, transition: { duration: 1 } }
            }}
          >
            A Purpose-Native enterprise is one where people know not only what
            they are doing, but why they are doing it — and how each step connects
            to the larger mission. It aligns thinking, actions, habits, and
            decisions in a way that becomes self-sustaining.
          </motion.p>

          <motion.p
            variants={{
              hidden: { opacity: 0, y: 20 },
              visible: { opacity: 1, y: 0, transition: { duration: 1 } }
            }}
          >
            This is the heart of our philosophy: when leaders operate with clarity
            of purpose, consistency of behavior, and visibility of progress, they
            create environments where teams thrive, outcomes accelerate, and
            growth becomes inevitable.
          </motion.p>
        </motion.div> */}
      </div>

      {/* --- Motion Keyframes --- */}
      <style>{`
        @keyframes fogFloat {
          0% { transform: translateY(0px); opacity: 0.6; }
          50% { transform: translateY(-20px); opacity: 0.75; }
          100% { transform: translateY(0px); opacity: 0.6; }
        }
        .animate-fog-float {
          animation: fogFloat 14s ease-in-out infinite;
        }

        @keyframes philoSpark {
          0% { transform: translateY(0) scale(0.7); opacity: 0.2; }
          50% { transform: translateY(-25px) scale(1); opacity: 0.5; }
          100% { transform: translateY(0) scale(0.7); opacity: 0.2; }
        }
        .animate-philo-spark {
          animation: philoSpark linear infinite;
        }
      `}</style>

    </section>
  );
}